import { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Shared coin balance, persisted to localStorage so the header, home page,
// and the play screen all read/write ONE source of truth (via context).
const COINS_KEY = 'finlite_coins';
const WELCOME_BONUS = 500; // starting credits for brand-new visitors

const CoinsContext = createContext(null);

export function CoinsProvider({ children }) {
  const [coins, setCoins] = useState(() => {
    const raw = localStorage.getItem(COINS_KEY);
    if (raw === null) return WELCOME_BONUS; // first visit
    const saved = Number(raw);
    return Number.isFinite(saved) ? saved : 0;
  });

  // Persist on change.
  useEffect(() => {
    localStorage.setItem(COINS_KEY, String(coins));
  }, [coins]);

  // Sync across other tabs/windows.
  useEffect(() => {
    const sync = (e) => {
      if (e.key === COINS_KEY) {
        const saved = Number(e.newValue);
        if (Number.isFinite(saved)) setCoins(saved);
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const value = useMemo(
    // Clamp at 0 so the balance can never go negative, regardless of caller.
    () => ({ coins, setCoins, addCoins: (n) => setCoins((c) => Math.max(0, c + n)) }),
    [coins],
  );

  return <CoinsContext.Provider value={value}>{children}</CoinsContext.Provider>;
}

export function useCoins() {
  const ctx = useContext(CoinsContext);
  if (!ctx) throw new Error('useCoins must be used within a CoinsProvider');
  return ctx;
}
